import { ApiError } from '@/api/client'
import {
  fetchIngredientCatalog,
  useCreateIngredient,
  useDeleteIngredient,
  useIngredients,
  useUpdateIngredient,
} from '@/api/hooks'
import type { FoodSearchResult, Ingredient } from '@/api/types'
import { cn } from '@/lib/cn'
import { Button, IconButton } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import {
  EmptyState,
  ErrorNote,
  FormError,
  SkeletonRows,
} from '@/components/ui/Feedback'
import { Input, NumberInput, UnitInput } from '@/components/ui/Field'
import { Menu } from '@/components/ui/Menu'
import { ConfirmDialog, Dialog } from '@/components/ui/Overlay'
import { Pagination } from '@/components/ui/Pagination'
import { useToast } from '@/components/ui/toastContext'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { SearchField } from '@/features/recipes/FilterBar'
import { FoodLookupDialog } from '@/features/ingredients/FoodLookupDialog'
import { formatMacro, pluralise } from '@/lib/format'
import { downloadJson } from '@/lib/download'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { ArrowDown, ArrowUp, BookOpen, Download, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, createSearchParams, useSearchParams } from 'react-router-dom'

const PAGE_SIZE = 50

/** Column order mirrors the table; keys are the backend's SORT_COLUMNS. */
const COLUMNS: Array<{ key: string; label: string; numeric: boolean }> = [
  { key: 'name', label: 'Name', numeric: false },
  { key: 'calories', label: 'kcal', numeric: true },
  { key: 'protein', label: 'Protein', numeric: true },
  { key: 'carbs', label: 'Carbs', numeric: true },
  { key: 'fat', label: 'Fat', numeric: true },
  { key: 'fiber', label: 'Fibre', numeric: true },
  { key: 'recipe_count', label: 'Used in', numeric: false },
]

type Draft = {
  name: string
  calories: number | null
  protein: number | null
  carbs: number | null
  fat: number | null
  fiber: number | null
  conversion_unit: string
  grams_per_unit: number | null
}

const EMPTY_DRAFT: Draft = {
  name: '',
  calories: null,
  protein: null,
  carbs: null,
  fat: null,
  fiber: null,
  conversion_unit: '',
  grams_per_unit: null,
}

function draftFrom(ingredient: Ingredient): Draft {
  return {
    name: ingredient.name,
    calories: ingredient.calories,
    protein: ingredient.protein,
    carbs: ingredient.carbs,
    fat: ingredient.fat,
    fiber: ingredient.fiber,
    conversion_unit: ingredient.conversion_unit ?? '',
    grams_per_unit: ingredient.grams_per_unit,
  }
}

/**
 * Ingredient library. Macro columns are all optional: an ingredient can exist
 * as a name only, which is how spices and garnishes are handled.
 */
export function IngredientsPage() {
  useDocumentTitle('Ingredients')
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const untrackedOnly = params.get('untracked') === 'yes'
  const offset = Math.max(0, Number(params.get('offset')) || 0)
  const sort = params.get('sort') ?? 'name'
  const order = params.get('order') === 'desc' ? 'desc' : 'asc'

  const query = useIngredients({
    q: q || undefined,
    untracked_only: untrackedOnly,
    sort,
    order,
    limit: PAGE_SIZE,
    offset,
  })

  const create = useCreateIngredient()
  const save = useUpdateIngredient()

  const [editing, setEditing] = useState<Ingredient | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Ingredient | null>(null)

  function update(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, value)
    }
    setParams(next, { replace: true })
  }

  function setOffset(next: number) {
    update(next === 0 ? { offset: null } : { offset: String(next) })
  }

  /** Clicking the active column flips the direction, any other starts ascending. */
  function onSort(column: string) {
    const next = sort === column && order === 'asc' ? 'desc' : 'asc'
    update({
      sort: column === 'name' ? null : column,
      order: next === 'desc' ? 'desc' : null,
      offset: null,
    })
  }

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  async function exportCatalog() {
    try {
      downloadJson('ingredient-catalog.json', await fetchIngredientCatalog())
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not export.')
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Ingredients"
        subtitle={`${pluralise(total, 'ingredient')} in the library`}
        actions={
          <>
            <Menu
              items={[
                {
                  label: 'Export catalog',
                  icon: <Download size={16} />,
                  onSelect: () => void exportCatalog(),
                },
              ]}
            />
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus size={16} aria-hidden />
              New ingredient
            </Button>
          </>
        }
      />

      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          <SearchField
            value={q}
            onChange={(value) => update({ q: value, offset: null })}
            placeholder="Name contains…"
          />
          <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={untrackedOnly}
              onChange={(event) =>
                update({
                  untracked: event.target.checked ? 'yes' : null,
                  offset: null,
                })
              }
              className="size-4 rounded"
            />
            Without macro data only
          </label>
        </div>

        {query.isError ? (
          <ErrorNote
            body={query.error instanceof ApiError ? query.error.message : undefined}
            onRetry={() => void query.refetch()}
          />
        ) : query.isPending ? (
          <SkeletonRows rows={8} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No ingredients match"
            body="Clear the search, or add something new."
          />
        ) : (
          <Card className="overflow-hidden">
            <div className="scroll-slim overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-sunken">
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={
                          sort === column.key
                            ? order === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                        className={cn(
                          'px-3 py-2',
                          column.numeric ? 'text-right' : 'text-left',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => onSort(column.key)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-ink-faint hover:text-ink"
                        >
                          {column.label}
                          {sort === column.key ? (
                            order === 'asc' ? (
                              <ArrowUp size={12} aria-hidden />
                            ) : (
                              <ArrowDown size={12} aria-hidden />
                            )
                          ) : null}
                        </button>
                      </th>
                    ))}
                    <th scope="col" className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((ingredient) => {
                    const tracked =
                      ingredient.calories !== null ||
                      ingredient.protein !== null ||
                      ingredient.carbs !== null ||
                      ingredient.fat !== null ||
                      ingredient.fiber !== null
                    return (
                      <tr
                        key={ingredient.id}
                        className="border-b border-line last:border-0 hover:bg-surface-sunken/70"
                      >
                        <td className="px-3 py-2 font-medium text-ink">
                          {ingredient.name}
                          {!tracked ? (
                            <Badge tone="outline" className="ml-2">
                              untracked
                            </Badge>
                          ) : null}
                        </td>
                        <td className="tabular px-3 py-2 text-right text-ink-soft">
                          {formatMacro(ingredient.calories)}
                        </td>
                        <td className="tabular px-3 py-2 text-right text-ink-soft">
                          {formatMacro(ingredient.protein)}
                        </td>
                        <td className="tabular px-3 py-2 text-right text-ink-soft">
                          {formatMacro(ingredient.carbs)}
                        </td>
                        <td className="tabular px-3 py-2 text-right text-ink-soft">
                          {formatMacro(ingredient.fat)}
                        </td>
                        <td className="tabular px-3 py-2 text-right text-ink-soft">
                          {formatMacro(ingredient.fiber)}
                        </td>
                        <td className="px-3 py-2 text-ink-soft">
                          {ingredient.recipe_count > 0 ? (
                            <Link
                              to={{
                                pathname: '/recipes',
                                search: createSearchParams({
                                  q: `ingredient:${ingredient.name}`,
                                }).toString(),
                              }}
                              className="hover:text-accent"
                            >
                              {pluralise(ingredient.recipe_count, 'recipe')}
                            </Link>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-0.5">
                            <IconButton
                              label={`Edit ${ingredient.name}`}
                              onClick={() => setEditing(ingredient)}
                            >
                              <Pencil size={14} />
                            </IconButton>
                            <IconButton
                              label={`Delete ${ingredient.name}`}
                              variant="danger"
                              onClick={() => setDeleting(ingredient)}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Pagination total={total} offset={offset} pageSize={PAGE_SIZE} onChange={setOffset} />
      </div>

      <IngredientDialog
        open={creating}
        onClose={() => setCreating(false)}
        title="New ingredient"
        submitLabel="Create"
        onSubmit={async (draft) => {
          await create.mutateAsync(draft)
        }}
      />

      <IngredientDialog
        key={editing?.id ?? 'none'}
        open={editing !== null}
        ingredient={editing}
        onClose={() => setEditing(null)}
        title={`Edit ${editing?.name ?? ''}`}
        submitLabel="Save"
        onSubmit={async (draft) => {
          if (!editing) return
          await save.mutateAsync({ id: editing.id, ...draft })
        }}
      />

      <DeleteIngredientDialog ingredient={deleting} onClose={() => setDeleting(null)} />
    </PageShell>
  )
}

function IngredientDialog({
  open,
  ingredient,
  title,
  submitLabel,
  onClose,
  onSubmit,
}: {
  open: boolean
  ingredient?: Ingredient | null
  title: string
  submitLabel: string
  onClose: () => void
  onSubmit: (draft: Draft) => Promise<void>
}) {
  const [draft, setDraft] = useState<Draft>(
    ingredient ? draftFrom(ingredient) : EMPTY_DRAFT,
  )
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lookingUp, setLookingUp] = useState(false)

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  /** Copy a looked-up food into the form. Nothing is written until Submit. */
  function applyLookup(food: FoodSearchResult) {
    setLookingUp(false)
    setDraft((current) => ({
      ...current,
      name: food.description,
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
      fiber: food.fiber,
    }))
  }

  async function submit() {
    if (!draft.name.trim()) {
      setError('Name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit({ ...draft, name: draft.name.trim() })
      onClose()
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not save the ingredient.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description="Leave the macro fields empty for ingredients you do not count."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormError>{error}</FormError>
        <Input
          label="Name"
          value={draft.name}
          onChange={(event) => set('name', event.target.value)}
          data-autofocus
        />
        {/* Offered when creating only: editing is for correcting macros you
            already trust, and overwriting them from a different food entry is a
            footgun. */}
        {ingredient ? null : (
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLookingUp(true)}
            >
              <BookOpen size={14} aria-hidden />
              Look up on USDA FoodData Central
            </Button>
            <p className="mt-1.5 text-micro text-pretty text-ink-faint">
              Fills the name and the macros below. Nothing is saved until you
              submit.
            </p>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              ['calories', 'Calories', 'per 100g', 1],
              ['protein', 'Protein', 'g per 100g', 0.1],
              ['carbs', 'Carbs', 'g per 100g', 0.1],
              ['fat', 'Fat', 'g per 100g', 0.1],
              ['fiber', 'Fibre', 'g per 100g', 0.1],
            ] as const
          ).map(([key, label, suffix, step]) => (
            <NumberInput
              key={key}
              label={label}
              value={draft[key]}
              onValueChange={(value) => set(key, value)}
              suffix={suffix}
              step={step}
              min={0}
            />
          ))}
        </div>
        {/* Optional. Only ever a suggestion the recipe form offers to apply. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <UnitInput
            label="Counted by"
            value={draft.conversion_unit}
            onChange={(event) => set('conversion_unit', event.target.value)}
            placeholder="can, clove, slice…"
          />
          <NumberInput
            label="Grams each"
            value={draft.grams_per_unit}
            onValueChange={(value) => set('grams_per_unit', value)}
            placeholder="400"
            min={0}
          />
        </div>
        <p className="text-micro text-pretty text-ink-faint">
          Optional. Lets a recipe fill in grams when you count this ingredient by the unit.
        </p>
      </div>

      {/* Opens on top of this dialog; the draft above is untouched until the user
          picks a food. Mounted only while open, so it always re-seeds from the
          name in the form without an effect to reset it. */}
      {lookingUp ? (
        <FoodLookupDialog
          open
          query={draft.name}
          onClose={() => setLookingUp(false)}
          onSelect={applyLookup}
        />
      ) : null}
    </Dialog>
  )
}

function DeleteIngredientDialog({
  ingredient,
  onClose,
}: {
  ingredient: Ingredient | null
  onClose: () => void
}) {
  const remove = useDeleteIngredient()
  const toast = useToast()
  const [blocking, setBlocking] = useState<string | null>(null)

  if (!ingredient) return null

  async function confirm() {
    if (!ingredient) return
    try {
      await remove.mutateAsync(ingredient.id)
      toast.success(`${ingredient.name} deleted.`)
      onClose()
      setBlocking(null)
    } catch (error) {
      if (error instanceof ApiError && error.code === 'conflict') {
        const recipes = error.context['recipes']
        if (Array.isArray(recipes)) {
          setBlocking(
            `Still used by: ${recipes
              .map((item) => (item as { name: string }).name)
              .join(', ')}. Remove it from those recipes first.`,
          )
          return
        }
      }
      toast.error(error instanceof ApiError ? error.message : 'Could not delete.')
    }
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => void confirm()}
      loading={remove.isPending}
      destructive
      confirmLabel="Delete ingredient"
      title={`Delete ${ingredient.name}?`}
      body={
        <div className="space-y-2">
          <p>This cannot be undone.</p>
          {blocking ? <p className="text-critical">{blocking}</p> : null}
        </div>
      }
    />
  )
}
